import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import {
  Box,
  TextField,
  Button,
  FormControl,
  CircularProgress,
  Typography,
  IconButton,
  FormHelperText,
  Alert
} from '@mui/material';
import PropTypes from 'prop-types';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import { useTranslation } from 'react-i18next';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker/DatePicker';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import DropdownMenu from 'src/components/DropdownMenu';
import { PaymentFormList } from 'src/utils/constants/transactions';
import Preloader from 'src/components/Preloader';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { loadClientContracts } from 'src/store/actions/clientActions';
import ContractStatus from 'src/utils/constants/contracts';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import {
  invoiceTypeLabels,
  PaymentMethod,
  InvoiceTypes,
  getCancelWarningKey,
  categoryOptions
} from 'src/utils/constants/cfdi';
import { getPaymentMethod } from 'src/utils/constants/parameters';
import makeInitialState from './initialState';
import validationSchema from './schema';

const StepTwo = ({
  data,
  handleSubmit,
  handleBack,
  loading,
  resetRef,
  isOlderThan72Hours,
  isAmountMoreThan5000
}) => {
  const { t } = useTranslation();
  const initialValues = makeInitialState(data);
  const dispatch = useDispatch();
  const [cfdiList, setCfdiList] = useState([]);
  const [cfdisCount, setCfdisCount] = useState(1);
  const isLoadingCfdis = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );
  const warningKeyForCancelDependingOnApproveOfClient = getCancelWarningKey(
    isOlderThan72Hours,
    isAmountMoreThan5000
  );

  const onNext = useCallback(
    async values => {
      const requestData = {
        ...values,
        relatedDocuments:
          values.invoiceType === InvoiceTypes.PAYMENT
            ? values.cfdis.map(cfdi => {
                return {
                  invoiceId: cfdi.id,
                  amount: cfdi.amountIvaIncluded
                };
              })
            : values.relatedDocuments,
        amount:
          values.invoiceType === InvoiceTypes.PAYMENT
            ? values.totalAmount
            : values.amount
      };

      delete requestData.cfdis;

      handleSubmit(requestData);
    },
    [handleSubmit]
  );

  const incrementInputCount = (setFieldValue, values) => {
    return () => {
      if (cfdisCount < cfdiList.length) {
        setFieldValue('cfdis', [...values.cfdis, null]);
      }
      setCfdisCount(preValue => preValue + 1);
    };
  };

  const createHandleRemoveCfdi = (index, setFieldValue, values) => () => {
    const updatedCfdis = values.cfdis.filter((_, i) => i !== index);
    setFieldValue('cfdis', updatedCfdis);
    setCfdisCount(preValue => preValue - 1);
  };

  useEffect(() => {
    if (data.invoiceType === InvoiceTypes.PAYMENT && data.clientId) {
      dispatch(
        loadClientContracts(
          { clientId: data.clientId, status: ContractStatus.IN_PROGRESS },
          false
        )
      );
    }
    if (data.pendingPayments && data.pendingPayments.length > 0) {
      setCfdisCount(data.pendingPayments.length);
      setCfdiList(data.pendingPayments);
    }
    return () => {
      setCfdisCount(1);
      setCfdiList([]);
    };
  }, [dispatch, data.clientId, data.invoiceType, data.pendingPayments]);

  return (
    <>
      {loading ? (
        <Preloader />
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onNext}
          validateOnChange
          validateOnBlur
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            setFieldValue,
            handleSubmit: formikHandleSubmit,
            resetForm
          }) => {
            resetRef.current = () => resetForm();
            return (
              <form onSubmit={formikHandleSubmit}>
                <Box display="flex" flexDirection="column">
                  {warningKeyForCancelDependingOnApproveOfClient && (
                    <Alert severity="warning" sx={{ marginBottom: '1rem' }}>
                      {t(warningKeyForCancelDependingOnApproveOfClient)}
                    </Alert>
                  )}
                  <FormControl fullWidth>
                    <TextField
                      id="folio"
                      error={Boolean(touched.folio && errors.folio)}
                      fullWidth
                      helperText={touched.folio && t(errors.folio)}
                      label={t('CFDI_FORM.FOLIO')}
                      margin="normal"
                      name="folio"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.folio}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="invoiceType"
                      error={Boolean(touched.invoiceType && errors.invoiceType)}
                      fullWidth
                      helperText={touched.invoiceType && t(errors.invoiceType)}
                      label={t('CFDI_FORM.INVOICE_TYPE')}
                      margin="normal"
                      name="invoiceType"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={invoiceTypeLabels[values.invoiceType]}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="contractId"
                      error={Boolean(touched.contractId && errors.contractId)}
                      fullWidth
                      helperText={touched.contractId && t(errors.contractId)}
                      label={t('CFDI_FORM.CONTRACT_ID')}
                      margin="normal"
                      name="contractId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.contractId}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="clientId"
                      error={Boolean(touched.clientId && errors.clientId)}
                      fullWidth
                      helperText={touched.clientId && t(errors.clientId)}
                      label={t('CFDI_FORM.CLIENT_ID')}
                      margin="normal"
                      name="clientId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.clientId}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="clientName"
                      error={Boolean(touched.clientName && errors.clientName)}
                      fullWidth
                      helperText={touched.clientName && t(errors.clientName)}
                      label={t('CFDI_FORM.CLIENT_NAME')}
                      margin="normal"
                      name="clientName"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.clientName}
                      variant="outlined"
                    />
                  </FormControl>

                  {values.invoiceType !== InvoiceTypes.PAYMENT && (
                    <>
                      <FormControl fullWidth>
                        <TextField
                          id="comment"
                          error={Boolean(touched.comment && errors.comment)}
                          fullWidth
                          helperText={touched.comment && t(errors.comment)}
                          label={t('CFDI_FORM.DESCRIPTION')}
                          margin="normal"
                          name="comment"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.comment}
                          variant="outlined"
                        />
                      </FormControl>
                      <FormControl fullWidth>
                        <DropdownMenu
                          list={categoryOptions.map(category => {
                            return {
                              key: category,
                              name: `${t(`TRANSACTION.CATEGORY.${category}`)}`
                            };
                          })}
                          value={values.category}
                          setValue={value => {
                            setFieldValue('category', value);
                          }}
                          label={t('COMMON.CATEGORY')}
                          defaultValue={values.category}
                          error={Boolean(touched.category && errors.category)}
                          errorText={touched.category && t(errors.category)}
                        />
                      </FormControl>
                      <FormControl fullWidth>
                        <TextField
                          id="amount"
                          InputProps={{
                            autoComplete: 'off',
                            inputComponent: MoneyMask,
                          }}
        InputLabelProps={{ shrink: true }}
                          error={Boolean(touched.amount && errors.amount)}
                          fullWidth
                          helperText={touched.amount && t(errors.amount)}
                          label={t('CFDI_FORM.AMOUNT_WITH_IVA')}
                          margin="normal"
                          name="amount"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.amount}
                          variant="outlined"
                        />
                      </FormControl>
                      <FormControl fullWidth>
                        <DropdownMenu
                          list={getPaymentMethod(t)}
                          value={values.paymentMethod}
                          setValue={value => {
                            setFieldValue('paymentMethod', value);
                          }}
                          label={t('CFDI_FORM.PAYMENT_METHOD')}
                          error={Boolean(
                            touched.paymentMethod && errors.paymentMethod
                          )}
                          errorText={
                            touched.paymentMethod && t(errors.paymentMethod)
                          }
                          key={values.paymentMethod}
                        />
                      </FormControl>

                      {values.paymentMethod === PaymentMethod.PUE && (
                        <>
                          <FormControl fullWidth>
                            <DropdownMenu
                              list={PaymentFormList}
                              value={values.paymentForm}
                              name="paymentForm"
                              setValue={value =>
                                setFieldValue('paymentForm', value)
                              }
                              label={t('CFDI_FORM.PAYMENT_FORM')}
                              error={Boolean(
                                touched.paymentForm && errors.paymentForm
                              )}
                              errorText={
                                touched.paymentForm && t(errors.paymentForm)
                              }
                            />
                          </FormControl>
                          <FormControl fullWidth>
                            <DatePicker
                              label={t('CFDI_FORM.PAYMENT_DATE')}
                              format="DD/MM/YYYY"
                              value={values.paymentDate}
                              onChange={newValue => {
                                setFieldValue(
                                  'paymentDate',
                                  momentLocalTimezone(newValue)
                                );
                              }}
                              slotProps={{
                                textField: {
                                  sx: { width: '100%', margin: '16px 0' },
                                  error: touched.paymentDate && Boolean(errors.paymentDate),
                                  helperText:
                                    touched.paymentDate && errors.paymentDate
                                      ? t(errors.paymentDate)
                                      : '',
                                  FormHelperTextProps: { sx: { color: 'red' } },
                                  InputProps: { autoComplete: 'off' }
                                }
                              }}
                            />
                          </FormControl>
                        </>
                      )}
                    </>
                  )}

                  {values.invoiceType === InvoiceTypes.PAYMENT && (
                    <>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        {t('COMMON.PAYMENT_DETAILS')}
                      </Typography>

                      <FormControl fullWidth>
                        <Box
                          sx={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            padding: '8px',
                            marginBottom: '16px'
                          }}
                        >
                          {Array(cfdisCount)
                            .fill('')
                            .map((input, index) => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  flexDirection: 'column',
                                  marginTop: '16px',
                                  padding: '8px',
                                  borderBottom:
                                    index < cfdisCount - 1
                                      ? '1px solid #f0f0f0'
                                      : 'none'
                                }}
                                // eslint-disable-next-line react/no-array-index-key
                                key={`box-${index}`}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                  }}
                                >
                                  <Autocomplete
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`cfdi-${index}$`}
                                    options={cfdiList.filter(
                                      cfdi =>
                                        !values.cfdis.some(selectedCfdi => {
                                          if (selectedCfdi) {
                                            return selectedCfdi.id === cfdi.id;
                                          }
                                          return false;
                                        })
                                    )}
                                    id="cfdis"
                                    label={t('COMMON.CFDI')}
                                    value={values.cfdis[index] ?? null}
                                    loading={isLoadingCfdis}
                                    disabled={
                                      isLoadingCfdis || !values.contractId
                                    }
                                    loadingText={t('COMMON.LOADING')}
                                    getOptionLabel={option =>
                                      option
                                        ? `${t('COMMON.SERIE')} ${
                                            option.series
                                          } - ${
                                            option.folio
                                          } - $${formatAmountWithDecimals(
                                            option?.amountIvaIncluded
                                          )}`
                                        : ''
                                    }
                                    onChange={(_e, value) => {
                                      const updatedCfdis = [...values.cfdis];
                                      if (value) {
                                        updatedCfdis[index] = value
                                          ? {
                                              ...value,
                                              amountToPay:
                                                value?.amountIvaIncluded
                                            }
                                          : null;
                                      }
                                      setFieldValue('cfdis', updatedCfdis);
                                    }}
                                    error={Boolean(
                                      touched.cfdis &&
                                        errors.cfdis &&
                                        errors.cfdis[index]
                                    )}
                                    sx={{
                                      width: '60%'
                                    }}
                                  />

                                  <TextField
                                    sx={{
                                      width: '32%'
                                    }}
                                    InputProps={{
                                      autoComplete: 'off',
                                      inputComponent: MoneyMask,
                                    }}
        InputLabelProps={{ shrink: true }}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`amount-${index}`}
                                    error={Boolean(
                                      touched.cfdis &&
                                        errors.cfdis &&
                                        errors.cfdis[index]
                                    )}
                                    disabled={!values.cfdis[index]}
                                    label={t('COMMON.AMOUNT_WITH_IVA')}
                                    onBlur={handleBlur}
                                    name={`cfdis[${index}].amountIvaIncluded`}
                                    onChange={e => {
                                      const newValues = [...values.cfdis];
                                      if (
                                        values.cfdis[index]
                                          ?.amountIvaIncluded ||
                                        values.cfdis[index]
                                          ?.amountIvaIncluded === 0
                                      ) {
                                        if (
                                          e.target.value ||
                                          e.target.value === 0
                                        ) {
                                          newValues[index] = {
                                            ...newValues[index],
                                            amountIvaIncluded: e.target.value
                                          };
                                        }
                                      }

                                      setFieldValue('cfdis', newValues);
                                    }}
                                    value={
                                      values.cfdis[index]?.amountIvaIncluded ??
                                      ''
                                    }
                                    variant="outlined"
                                  />
                                  <IconButton
                                    sx={{
                                      width: '20px',
                                      height: '20px',
                                      paddingLeft: '3px'
                                    }}
                                    aria-label="delete"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`delete-${index}`}
                                    disabled={cfdisCount === 1}
                                    onClick={createHandleRemoveCfdi(
                                      index,
                                      setFieldValue,
                                      values
                                    )}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>

                                <FormHelperText error>
                                  {touched.cfdis &&
                                  errors.cfdis &&
                                  errors.cfdis[index]
                                    ? t(errors.cfdis[index])
                                    : ' '}
                                </FormHelperText>
                              </Box>
                            ))}
                        </Box>
                      </FormControl>

                      <Box
                        key="addButton"
                        sx={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Button
                          type="button"
                          key="addButton"
                          color="primary"
                          variant="text"
                          disabled={
                            isLoadingCfdis || cfdisCount > cfdiList.length
                          }
                          onClick={incrementInputCount(setFieldValue, values)}
                          startIcon={<AddIcon />}
                        >
                          {t('COMMON.ADD')}
                        </Button>
                      </Box>

                      <TextField
                        fullWidth
                        margin="normal"
                        name="totalAmount"
                        id="totalAmount"
                        disabled
                        label={t('COMMON.TOTAL_AMOUNT')}
                        InputProps={{
                          readOnly: true,
                          inputComponent: MoneyMask,
                        }}
        InputLabelProps={{ shrink: true }}
                        value={formatAmountWithDecimals(
                          values.cfdis.reduce((acc, cfdi) => {
                            if (cfdi?.amountIvaIncluded) {
                              return acc + cfdi.amountIvaIncluded;
                            }
                            return acc;
                          }, 0)
                        )}
                      />

                      <FormControl fullWidth>
                        <DropdownMenu
                          list={PaymentFormList}
                          value={values.paymentForm}
                          name="paymentForm"
                          setValue={value =>
                            setFieldValue('paymentForm', value)
                          }
                          label={t('TRANSACTION.PAYMENT_FORM')}
                          error={Boolean(
                            touched.paymentForm && errors.paymentForm
                          )}
                          errorText={
                            touched.paymentForm && t(errors.paymentForm)
                          }
                        />
                      </FormControl>

                      <FormControl fullWidth>
                        <DatePicker
                          name="date"
                          label={t('TRANSACTION.DATE')}
                          value={values.date || null}
                          onChange={newValue => {
                            setFieldValue(
                              'date',
                              momentLocalTimezone(newValue)
                            );
                          }}
                          slotProps={{
                            textField: {
                              sx: { width: '100%', margin: '16px 0' },
                              error: Boolean(touched.date && errors.date),
                              helperText: touched.date && t(errors.date),
                              FormHelperTextProps: { sx: { color: 'tomato' } },
                              InputProps: { autoComplete: 'off' }
                            }
                          }}
                        />
                      </FormControl>
                    </>
                  )}

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mt={3}
                    sx={{ width: '100%' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{ minWidth: '45%' }}
                      disabled={loading}
                    >
                      {t('COMMON.BACK')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      sx={{ minWidth: '45%' }}
                    >
                      {loading ? (
                        <CircularProgress size={10} />
                      ) : (
                        t('COMMON.ACCEPT')
                      )}
                    </Button>
                  </Box>
                </Box>
              </form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

StepTwo.propTypes = {
  data: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  resetRef: PropTypes.object.isRequired,
  isOlderThan72Hours: PropTypes.bool.isRequired,
  isAmountMoreThan5000: PropTypes.bool.isRequired
};
export default StepTwo;
